/**
 * SessionNoteEditor — Editor inline de notas clínicas por sesión.
 *
 * Formato reducido (2026-09): 2 secciones.
 *  1. "Encabezado clínico" — 3 catálogos (categoría del motivo, ciclo
 *     terapéutico, etapa del ciclo vital).
 *  2. "Registro clínico de la sesión" — 4 observaciones de texto libre:
 *       Observación I   → observacion_clinica
 *       Observación II  → desarrollo_sesion
 *       Observación III → respuesta_evolucion
 *       Observación IV  → observaciones_adicionales  (opcional)
 *
 * Los demás campos que la RPC update_session_clinical_notes sabe recibir
 * (motivo específico, antecedentes, intervenciones, plan, evaluación de
 * continuidad, etc.) ya NO se editan acá — el caller los deja fuera del
 * payload y la RPC (COALESCE) preserva lo que se haya escrito antes. Las
 * columnas siguen vivas en `consultas`.
 *
 * Contexto B — Portal Terapeuta. Reusa las clases CSS ya definidas en
 * ClinicalRecords.css (cr-edit-*, cr-btn, cr-session-notes) + `cr-draft-*` (P1-9).
 *
 * UX_VISION P1-9 — autosave: `fichaPersistence.ts` guarda un borrador en
 * `sessionStorage` (`cc:ficha:draft:<consulta_id>`) con debounce mientras el
 * terapeuta edita, para que no pierda el trabajo si se cae la señal o cierra sin
 * querer. Revisado por `security` (engram `sec/p1-9-ficha-autosave-review`):
 * scope por `auth.uid()`, TTL corto, wipe en logout, banner sin preview de
 * valores, re-fetch fresco del server como baseline anti-clobber.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../../lib/supabase/client';
import { useToast } from '../ui/ToastProvider';
import {
  MOTIVO_CATEGORIAS,
  CICLO_TERAPEUTICO_OPTIONS,
  CICLO_VITAL_ETAPAS,
} from '../../lib/clinicalRecords/constants';
import type { SessionRecord, EditableSessionFields } from '../../lib/clinicalRecords/types';
import {
  type FichaFields,
  type FichaDraft,
  fieldsFrom,
  fieldsEqual,
  draftHasMeaningfulEdits,
  loadDraft,
  saveDraft,
  clearDraft,
  relativeTimeFromNow,
} from './fichaPersistence';

interface SessionNoteEditorProps {
  sesion: SessionRecord;
  // Historial completo del paciente (no solo la sesión que se edita) — se usa
  // únicamente para precargar ciclo_terapeutico/ciclo_vital_etapa desde la
  // sesión más reciente que ya los tenga cargados, así el terapeuta no tiene
  // que reseleccionarlos en cada sesión nueva.
  sesiones: SessionRecord[];
  therapistId: string;
  // Necesario para el re-fetch fresco anti-clobber del autosave (P1-9).
  patientId: string;
  onCancel: () => void;
  // Además de los campos editables, se devuelve el `updated_at` nuevo para que
  // el padre lo mergee en su `record.sesiones` — si no, tras un guardado el
  // `updated_at` de las props queda stale y un segundo guardado en la misma
  // vista falla con STALE_WRITE falso (guard OCC de BD-OCC-FICHA).
  onSaved: (updated: EditableSessionFields & { updated_at?: string }) => void;
}

// Busca, entre todas las sesiones del paciente, el valor más reciente
// (por fecha_hora) de un campo dado que no esté vacío. No asume el orden en
// que llega `sesiones` — lo ordena acá mismo por las dudas.
function findMostRecentValue(sesiones: SessionRecord[], key: 'ciclo_terapeutico' | 'ciclo_vital_etapa'): string {
  const ordenadasDesc = [...sesiones].sort(
    (a, b) => new Date(b.fecha_hora).getTime() - new Date(a.fecha_hora).getTime()
  );
  for (const s of ordenadasDesc) {
    const valor = s[key];
    if (valor) return valor;
  }
  return '';
}

const AUTOSAVE_DEBOUNCE_MS = 900;

type DraftUI =
  | { status: 'checking' }
  | { status: 'none' }
  | { status: 'offer'; draft: FichaDraft }
  | { status: 'conflict'; draft: FichaDraft }
  | { status: 'active'; conflicted: boolean };

function SessionNoteEditor({ sesion, sesiones, therapistId, patientId, onCancel, onSaved }: SessionNoteEditorProps) {
  const toast = useToast();

  // Semilla de los campos editables: estado del server + prefill de conveniencia de los 2 ciclos.
  const seedFields = useCallback((): FichaFields => {
    const raw = fieldsFrom(sesion);
    return {
      ...raw,
      ciclo_terapeutico: raw.ciclo_terapeutico || findMostRecentValue(sesiones, 'ciclo_terapeutico'),
      ciclo_vital_etapa: raw.ciclo_vital_etapa || findMostRecentValue(sesiones, 'ciclo_vital_etapa'),
    };
  }, [sesion, sesiones]);

  const [fields, setFields] = useState<FichaFields>(seedFields);
  const setField = (k: keyof FichaFields, v: string) => setFields((f) => ({ ...f, [k]: v }));

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ── Autosave / recuperación (UX_VISION P1-9) ──────────────
  const ownerIdRef = useRef<string>('');
  const editBaselineRef = useRef<FichaFields>(seedFields()); // dirty-check: ¿cambió algo desde que abrí?
  const serverBaselineRef = useRef<FichaFields>(fieldsFrom(sesion)); // anti-clobber: ¿se movió el server?
  // `consultas.updated_at` con el que se abrió el editor — se manda como
  // `p_expected_updated_at` para el guard OCC del server (BD-OCC-FICHA).
  // Se lee de `sesion.updated_at` (ya viene de get_patient_clinical_record/_admin),
  // NUNCA de un SELECT aparte a `consultas` — revisado por `security`: la policy
  // real de esa tabla puede ser más acotada que la RPC (que sí valida ownership
  // correctamente), y un SELECT directo podría devolver null en silencio y
  // desactivar el guard justo en el caso de mismatch de ownership fila-a-fila.
  const serverUpdatedAtRef = useRef<string | null>(sesion.updated_at ?? null);
  const createdAtRef = useRef<number>(Date.now());
  const savingRef = useRef(false);
  const autosaveBrokenRef = useRef(false);

  const [draftUI, setDraftUI] = useState<DraftUI>({ status: 'checking' });
  const [lastSavedAt, setLastSavedAt] = useState<number | null>(null);
  const [, forceTick] = useState(0);

  // Re-render periódico para refrescar el "guardado hace Xs".
  useEffect(() => {
    if (lastSavedAt == null) return;
    const id = window.setInterval(() => forceTick((n) => n + 1), 20_000);
    return () => window.clearInterval(id);
  }, [lastSavedAt]);

  // Chequeo inicial: usuario logueado + borrador + re-fetch fresco del server (baseline anti-clobber).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data: userData } = await supabase.auth.getUser();
      if (cancelled) return;
      const owner = userData?.user?.id ?? '';
      ownerIdRef.current = owner;
      if (!owner) {
        setDraftUI({ status: 'none' });
        return;
      }

      const draft = loadDraft(sesion.id, owner);
      if (!draft) {
        setDraftUI({ status: 'none' });
        return;
      }

      // NO confiar en las props: pueden estar stale (otro dispositivo guardó).
      let serverFields = fieldsFrom(sesion);
      try {
        const { data } = await supabase.rpc('get_patient_clinical_record', {
          p_patient_id: patientId,
          p_therapist_id: therapistId,
        });
        const fresh = (data as { sesiones?: Array<Record<string, unknown>> } | null)?.sesiones?.find(
          (s) => s.id === sesion.id
        );
        if (fresh) {
          serverFields = fieldsFrom(fresh);
          if (typeof fresh.updated_at === 'string') serverUpdatedAtRef.current = fresh.updated_at;
        }
      } catch {
        /* sin conexión / RPC caída → degradar al snapshot de props */
      }
      if (cancelled) return;

      serverBaselineRef.current = serverFields;

      if (fieldsEqual(serverFields, draft.fields) || !draftHasMeaningfulEdits(draft.fields, serverFields)) {
        // El borrador ya está guardado (o no aporta nada) → limpiar sin molestar.
        clearDraft(sesion.id);
        setDraftUI({ status: 'none' });
        return;
      }

      setDraftUI(
        fieldsEqual(serverFields, draft.baseline)
          ? { status: 'offer', draft }
          : { status: 'conflict', draft }
      );
    })();
    return () => {
      cancelled = true;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Autosave del borrador con debounce. Pausado durante el submit y si el storage falló.
  useEffect(() => {
    if (savingRef.current || autosaveBrokenRef.current) return;
    if (draftUI.status === 'checking' || draftUI.status === 'offer' || draftUI.status === 'conflict') return;
    if (!ownerIdRef.current) return;
    if (fieldsEqual(fields, editBaselineRef.current)) return;

    const t = window.setTimeout(() => {
      const at = saveDraft(sesion.id, {
        fields,
        baseline: serverBaselineRef.current,
        ownerAuthUserId: ownerIdRef.current,
        createdAt: createdAtRef.current,
      });
      if (at > 0) {
        setLastSavedAt(at);
        setDraftUI((s) => (s.status === 'none' ? { status: 'active', conflicted: false } : s));
      } else {
        autosaveBrokenRef.current = true;
        toast.info('No pudimos guardar el borrador en este dispositivo (almacenamiento lleno).');
      }
    }, AUTOSAVE_DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [fields, draftUI.status]); // eslint-disable-line react-hooks/exhaustive-deps

  const applyDraft = (draft: FichaDraft, conflicted: boolean) => {
    editBaselineRef.current = draft.fields; // recién restaurado → no re-guardar en el acto
    createdAtRef.current = draft.createdAt;
    setFields(draft.fields);
    setLastSavedAt(draft.savedAt);
    setDraftUI({ status: 'active', conflicted });
  };

  const discardDraft = () => {
    clearDraft(sesion.id);
    autosaveBrokenRef.current = false;
    editBaselineRef.current = seedFields();
    createdAtRef.current = Date.now();
    setFields(seedFields());
    setLastSavedAt(null);
    setDraftUI({ status: 'none' });
  };

  async function handleSave() {
    savingRef.current = true;
    setLoading(true);
    setError(null);
    // Solo se mandan los 7 campos del editor reducido (3 catálogos + 4
    // observaciones). Los demás params de la RPC son `DEFAULT NULL` y se
    // omiten a propósito: la RPC hace `campo = COALESCE(p_campo, campo)`, así
    // que omitirlos preserva lo que se haya escrito antes de la reducción del
    // formulario (motivo específico, antecedentes, plan, evaluación de
    // continuidad, etc.). Esas columnas siguen vivas en `consultas`.
    const { data: rpcData, error: rpcError } = await supabase.rpc('update_session_clinical_notes', {
      p_consulta_id: sesion.id,
      p_therapist_id: therapistId,
      p_motivo_categoria: fields.motivo_categoria,
      p_ciclo_terapeutico: fields.ciclo_terapeutico,
      p_ciclo_vital_etapa: fields.ciclo_vital_etapa,
      p_observacion_clinica: fields.observacion_clinica,          // Observación I
      p_desarrollo_sesion: fields.desarrollo_sesion,              // Observación II
      p_respuesta_evolucion: fields.respuesta_evolucion,          // Observación III
      p_observaciones_adicionales: fields.observaciones_adicionales, // Observación IV
      // OCC (BD-OCC-FICHA) — si el server tiene la RPC vieja (20 params) ignora
      // este arg; si tiene la nueva y la fila se movió, RAISE 'STALE_WRITE'.
      p_expected_updated_at: serverUpdatedAtRef.current ?? null,
    });
    if (rpcError) {
      const stale = /STALE_WRITE/i.test(rpcError.message ?? '');
      setError(
        stale
          ? 'La ficha se actualizó desde otra sesión mientras editabas. Tu borrador quedó guardado en este dispositivo — recargá la ficha para ver los cambios nuevos y volvé a aplicar los tuyos.'
          : 'No se pudo guardar la nota. Intentá nuevamente.'
      );
      setLoading(false);
      savingRef.current = false;
      return;
    }
    const newUpdatedAt = (rpcData as { updated_at?: string } | null)?.updated_at;
    if (newUpdatedAt) serverUpdatedAtRef.current = newUpdatedAt;
    clearDraft(sesion.id); // sincrónico, ANTES de onSaved (el editor se desmonta después)
    onSaved({ ...fields, ...(newUpdatedAt ? { updated_at: newUpdatedAt } : {}) });
  }

  const draftExists =
    draftUI.status === 'active' || draftUI.status === 'offer' || draftUI.status === 'conflict';

  return (
    <div className="cr-session-notes" id={`session-edit-${sesion.id}`}>
      {error && <p className="cr-edit-error" role="alert">{error}</p>}

      {/* Recuperación de borrador (P1-9) — sin preview de valores */}
      {draftUI.status === 'offer' && (
        <div className="cr-draft-banner" role="region" aria-label="Borrador sin guardar">
          <p className="cr-draft-banner__text">
            <i className="fas fa-rotate-left" aria-hidden="true" />
            Tenés cambios sin guardar en esta ficha, de {relativeTimeFromNow(draftUI.draft.savedAt)}.
          </p>
          <div className="cr-draft-banner__actions">
            <button type="button" className="cr-btn cr-btn--primary" onClick={() => applyDraft(draftUI.draft, false)}>
              Recuperar
            </button>
            <button type="button" className="cr-btn cr-btn--ghost" onClick={discardDraft}>
              Descartar
            </button>
          </div>
        </div>
      )}

      {draftUI.status === 'conflict' && (
        <div className="cr-draft-banner cr-draft-banner--warn" role="alert">
          <p className="cr-draft-banner__text">
            <i className="fas fa-triangle-exclamation" aria-hidden="true" />
            Tenés un borrador sin guardar, pero esta ficha se actualizó desde otro lugar mientras tanto.
            Si recuperás el borrador y guardás, vas a reemplazar esos cambios.
          </p>
          <div className="cr-draft-banner__actions">
            <button type="button" className="cr-btn cr-btn--ghost" onClick={() => applyDraft(draftUI.draft, true)}>
              Ver el borrador igual
            </button>
            <button type="button" className="cr-btn cr-btn--ghost" onClick={discardDraft}>
              Descartar borrador
            </button>
          </div>
        </div>
      )}

      {draftUI.status === 'active' && draftUI.conflicted && (
        <p className="cr-draft-banner cr-draft-banner--warn" role="alert">
          <i className="fas fa-triangle-exclamation" aria-hidden="true" />
          Estás editando sobre un borrador viejo. Al guardar vas a reemplazar lo que se guardó
          después desde otro lugar.
        </p>
      )}

      {/* Sección 1 — Encabezado clínico */}
      <h4 className="cr-edit-section__title">
        <i className="fas fa-clipboard-list" aria-hidden="true"></i>
        Encabezado clínico
      </h4>
      <div className="cr-edit-grid">
        <div className="cr-edit-field">
          <label className="cr-edit-label" htmlFor={`edit-motivo-categoria-${sesion.id}`}>Categoría del motivo</label>
          <select
            id={`edit-motivo-categoria-${sesion.id}`}
            className="cr-edit-input"
            value={fields.motivo_categoria}
            onChange={e => setField('motivo_categoria', e.target.value)}
          >
            <option value="">Sin especificar</option>
            {MOTIVO_CATEGORIAS.map(opcion => (
              <option key={opcion.value} value={opcion.value}>{opcion.label}</option>
            ))}
          </select>
        </div>
        <div className="cr-edit-field">
          <label className="cr-edit-label" htmlFor={`edit-ciclo-terapeutico-${sesion.id}`}>Ciclo terapéutico</label>
          <select
            id={`edit-ciclo-terapeutico-${sesion.id}`}
            className="cr-edit-input"
            value={fields.ciclo_terapeutico}
            onChange={e => setField('ciclo_terapeutico', e.target.value)}
          >
            <option value="">Sin especificar</option>
            {CICLO_TERAPEUTICO_OPTIONS.map(opcion => (
              <option key={opcion.value} value={opcion.value}>{opcion.label}</option>
            ))}
          </select>
        </div>
        <div className="cr-edit-field cr-edit-field--full">
          <label className="cr-edit-label" htmlFor={`edit-ciclo-vital-${sesion.id}`}>Etapa del ciclo vital</label>
          <select
            id={`edit-ciclo-vital-${sesion.id}`}
            className="cr-edit-input"
            value={fields.ciclo_vital_etapa}
            onChange={e => setField('ciclo_vital_etapa', e.target.value)}
          >
            <option value="">Sin especificar</option>
            {CICLO_VITAL_ETAPAS.map(opcion => (
              <option key={opcion.value} value={opcion.value}>{opcion.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Sección 2 — Registro clínico de la sesión (4 observaciones) */}
      <h4 className="cr-edit-section__title">
        <i className="fas fa-pen" aria-hidden="true"></i>
        Registro clínico de la sesión
      </h4>
      <div className="cr-edit-field">
        <label className="cr-edit-label" htmlFor={`edit-observacion-1-${sesion.id}`}>Observación I</label>
        <textarea
          id={`edit-observacion-1-${sesion.id}`}
          className="cr-edit-input cr-edit-input--textarea"
          rows={4}
          value={fields.observacion_clinica}
          onChange={e => setField('observacion_clinica', e.target.value)}
          placeholder="Primera observación de la sesión"
        />
      </div>
      <div className="cr-edit-field">
        <label className="cr-edit-label" htmlFor={`edit-observacion-2-${sesion.id}`}>Observación II</label>
        <textarea
          id={`edit-observacion-2-${sesion.id}`}
          className="cr-edit-input cr-edit-input--textarea"
          rows={4}
          value={fields.desarrollo_sesion}
          onChange={e => setField('desarrollo_sesion', e.target.value)}
          placeholder="Segunda observación de la sesión"
        />
      </div>
      <div className="cr-edit-field">
        <label className="cr-edit-label" htmlFor={`edit-observacion-3-${sesion.id}`}>Observación III</label>
        <textarea
          id={`edit-observacion-3-${sesion.id}`}
          className="cr-edit-input cr-edit-input--textarea"
          rows={4}
          value={fields.respuesta_evolucion}
          onChange={e => setField('respuesta_evolucion', e.target.value)}
          placeholder="Tercera observación de la sesión"
        />
      </div>
      <div className="cr-edit-field">
        <label className="cr-edit-label" htmlFor={`edit-observacion-4-${sesion.id}`}>Observación IV (opcional)</label>
        <textarea
          id={`edit-observacion-4-${sesion.id}`}
          className="cr-edit-input cr-edit-input--textarea"
          rows={4}
          value={fields.observaciones_adicionales}
          onChange={e => setField('observaciones_adicionales', e.target.value)}
          placeholder="Observación adicional, si corresponde"
        />
      </div>

      {/* Transparencia + control del borrador (P1-9) */}
      <p className="cr-draft-note">
        <i className="fas fa-floppy-disk" aria-hidden="true" />
        <span>
          Guardamos un borrador en este dispositivo mientras editás; se borra al guardar o al cerrar sesión.
          {lastSavedAt != null && (
            <> {' · '}Borrador guardado {relativeTimeFromNow(lastSavedAt)}.</>
          )}
          {draftExists && (
            <>
              {' '}
              <button type="button" className="cr-draft-discard" onClick={discardDraft}>
                Descartar borrador
              </button>
            </>
          )}
        </span>
      </p>

      <div className="cr-edit-actions">
        <button className="cr-btn cr-btn--primary" onClick={handleSave} disabled={loading}>
          {loading ? 'Guardando…' : 'Guardar'}
        </button>
        <button className="cr-btn cr-btn--ghost" onClick={onCancel} disabled={loading}>
          Cancelar
        </button>
      </div>
    </div>
  );
}

export default SessionNoteEditor;
